import React, { useState, useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Dna, Radar, Ruler,
  LayoutDashboard, Building2, Users, ClipboardList, BarChart3, LogOut, X,
  TrendingUp, Tag, Target, Settings, Trophy, Flag, Activity, Award, Sword,
  DollarSign, ShieldAlert, UserCog, MessageSquare, Link2, MessagesSquare,
  ChevronDown, FolderOpen, CalendarDays, Cake, Fingerprint, Clock, Brain,
  Presentation, Crown, UserSearch, Headset, Inbox, BookOpen,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useData } from '../contexts/DataContext.jsx';
import { prefetchRoute } from '../utils/routePrefetch.js';
import { isPontoEligible } from '../utils/ponto.js';
import { filtrarNavNativo, FIN_TRILHO_UNIDADE, OCULTAR_NATIVO, IA_ATENDENTE_MENU, OCULTAR_PRIVATES, ehRotaPrivates, CRM_SOFIA_MENU } from '../config/featureFlags.js';
import { useAtendimentoAcesso } from '../utils/useAtendimentoAcesso.js';
import { useFilaContador } from '../utils/useFilaContador.js';
import { useRecibosAcesso } from '../utils/useRecibosAcesso.js';
import { useOperadorFinanceiro } from '../utils/useOperadorFinanceiro.js';
import { useFinAlertas } from '../utils/useFinAlertas.js';
import { textoBadge } from '../utils/finAuditoriaFormat.js';
import { useAvisoAniversario, ROTA_ANIVERSARIOS } from '../utils/useAvisoAniversario.js';
import DNALogo from './DNALogo.jsx';
import { podeReunioes } from '../config/reunioesAcesso.js';
import { MANUAL_RECEPCAO_ROTA } from '../config/manualAcesso.js';

const ROTA_CRM = '/gestao/crm-sofia';

// Manual da Recepção (14/10/2026): o deck abre dentro do app (retrato no
// celular, paisagem no computador). Recepcionista e Gerente; os outros cargos
// não mudam (o master segue abrindo pelos decks de Reuniões).
const MANUAL_RECEPCAO_ITEM = { to: MANUAL_RECEPCAO_ROTA, icon: BookOpen, label: 'Manual da Recepção' };

// Estrutura nova com grupos:
//   - item direto: { to, icon, label, masterOnly? }
//   - grupo: { label, icon, children: [{ to, label }, ...], masterOnly? }
// filtrarNavNativo esconde a operação nativa (agenda/atendimento/validação)
// sem apagar nada — ver src/config/featureFlags.js pra reativar.
const adminNav = filtrarNavNativo([
  { to: '/gestao/dashboard',   icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/gestao/inteligencia', icon: Brain,          label: 'Inteligência' },
  { to: '/gestao/agenda',      icon: CalendarDays,    label: 'Agenda' },
  { to: '/gestao/radar',       icon: Radar,           label: 'Radar de Clientes' },
  // IA de atendimento, MODO TESTE (13/10/2026): só o master vê o grupo. A recepção e a
  // gerente ganham "CRM da Sofia" sozinho quando o servidor liberar (ver abaixo).
  // 26/10/2026, decisão do dono: "Pré-agendamentos" saiu do menu de todos (o pedido fica dentro
  // da conversa do CRM); a rota continua para os links antigos e os avisos sem conversa.
  ...(IA_ATENDENTE_MENU ? [{
    label: 'Atendimento IA',
    icon: Headset,
    masterOnly: true,
    children: [
      { to: '/gestao/atendimento-ia',   label: 'Simulador' },
      // sem o CRM no ar, a fila antiga continua no menu
      ...(CRM_SOFIA_MENU ? [] : [{ to: '/gestao/pre-agendamentos', label: 'Pré-agendamentos' }]),
      // CRM da Sofia (24/10/2026): só master neste grupo (a equipe tem o item próprio, abaixo)
      ...(CRM_SOFIA_MENU ? [{ to: '/gestao/crm-sofia', label: 'CRM', masterOnly: true }] : []),
    ],
  }] : []),
  { to: '/gestao/dna-360',     icon: Dna,             label: 'DNA 360', masterOnly: true },
  { to: '/gestao/reunioes',    icon: Presentation,    label: 'Reuniões', masterOnly: true, reunioes: true },
  { to: '/gestao/candidatos',  icon: UserSearch,      label: 'Candidatos', masterOnly: true },
  { to: '/gestao/privates',    icon: Crown,           label: 'Feel King Private', masterOnly: true },
  { to: '/gestao/atendimento', icon: MessagesSquare,  label: 'Atendimento' },
  { to: '/gestao/ranking',     icon: Trophy,          label: 'Ranking' },
  { to: '/gestao/duelos',      icon: Sword,           label: 'Duelos da Semana' },
  { to: '/gestao/aniversarios', icon: Cake,           label: 'Aniversários' },
  { to: '/gestao/metas',       icon: Target,          label: 'Meta x Realizado' },
  { to: '/gestao/desafios',    icon: Flag,            label: 'Desafios' },
  { to: '/gestao/financeiro',  icon: DollarSign,      label: 'Financeiro' },
  { to: '/gestao/banco-horas', icon: Clock,           label: 'Banco de Horas' },
  { to: '/gestao/avaliacoes',  icon: MessageSquare,   label: 'Avaliações' },
  {
    label: 'Cadastros',
    icon: FolderOpen,
    children: [
      { to: '/gestao/unidades',      label: 'Unidades' },
      { to: '/gestao/profissionais', label: 'Profissionais' },
      { to: '/gestao/clientes',      label: 'Clientes' },
      { to: '/gestao/servicos',      label: 'Serviços' },
      { to: '/gestao/estoque',       label: 'Estoque' },
    ],
  },
  {
    label: 'Relatórios',
    icon: BarChart3,
    children: [
      { to: '/gestao/relatorios',  label: 'Relatórios' },
      { to: '/gestao/engajamento', label: 'Engajamento' },
      // Auditoria = auth_log: só a direção (o servidor responde 403 pros demais).
      // 09/10/2026 (FIN_TRILHO_UNIDADE): "Registro de acessos" — "Auditoria" é a
      // do Financeiro (alertas), não confundir as duas.
      { to: '/gestao/auditoria',   label: FIN_TRILHO_UNIDADE ? 'Registro de acessos' : 'Auditoria', masterOnly: true },
      { to: '/gestao/relatorios/validacao-nativa', label: '🔬 Validação Nativa', masterOnly: true },
    ],
  },
  {
    label: 'Configurações',
    icon: Settings,
    masterOnly: true,
    children: [
      { to: '/gestao/configuracoes', label: 'Geral' },
      { to: '/gestao/integracoes',   label: 'Integrações' },
    ],
  },
]);

// Marketing e Assistente Adm têm o MESMO acesso do Administrativo (adminNav).
// Marketing apenas SEM Financeiro. (Assistente Adm cai no adminNav direto.)
// 06/10/2026: no adminNav o "Financeiro" só aparece pro OPERADOR financeiro
// (lista fechada no servidor — ver useOperadorFinanceiro no Sidebar abaixo).
const marketingNav = adminNav.filter(item =>
  item.to !== '/gestao/financeiro' && item.to !== '/gestao/banco-horas' && item.to !== '/gestao/inteligencia'
  && item.to !== '/gestao/radar' // PII de cliente (telefones) fica com recepção/gestão
);

// Sócio: vê tudo da rede MENOS Financeiro, Cadastros, Auditoria, Configurações,
// Atendimento e Agenda. Não tem unidade, salário, agenda e não entra em ranking/duelos.
const socioNav = [
  { to: '/gestao/dashboard',  icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/gestao/inteligencia', icon: Brain,         label: 'Inteligência' },
  { to: '/gestao/relatorios', icon: BarChart3,       label: 'Relatórios' },
  { to: '/gestao/ranking',    icon: Trophy,          label: 'Ranking' },
  { to: '/gestao/duelos',     icon: Sword,           label: 'Duelos da Semana' },
  { to: '/gestao/metas',      icon: Target,          label: 'Meta x Realizado' },
  { to: '/gestao/desafios',   icon: Flag,            label: 'Desafios' },
  { to: '/gestao/engajamento', icon: Activity,       label: 'Engajamento' },
  { to: '/gestao/avaliacoes', icon: MessageSquare,   label: 'Avaliações' },
  { to: '/gestao/aniversarios', icon: Cake,          label: 'Aniversários' },
];

// Nav da Gerente (subset do admin)
const gerenteNav = filtrarNavNativo([
  { to: '/gestao/meu-desempenho', icon: TrendingUp,    label: 'Meu Desempenho' },
  { to: '/gestao/dashboard',      icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/gestao/inteligencia',   icon: Brain,         label: 'Inteligência' },
  { to: '/gestao/agenda',         icon: CalendarDays,  label: 'Agenda' },
  { to: '/gestao/radar',          icon: Radar,         label: 'Radar de Clientes' },
  { to: '/gestao/privates',       icon: Crown,         label: 'Feel King Private' },
  { to: '/gestao/atendimento',    icon: MessagesSquare, label: 'Atendimento' },
  { to: '/gestao/ranking',        icon: Trophy,        label: 'Ranking' },
  { to: '/gestao/duelos',         icon: Sword,         label: 'Duelos da Semana' },
  { to: '/gestao/aniversarios',   icon: Cake,          label: 'Aniversários' },
  { to: '/gestao/metas',          icon: Target,        label: 'Meta x Realizado' },
  { to: '/gestao/avaliacoes',     icon: MessageSquare, label: 'Avaliações' },
  { to: '/gestao/profissionais',  icon: Users,         label: 'Profissionais' },
  { to: '/gestao/engajamento',    icon: Activity,      label: 'Engajamento' },
  { to: '/gestao/desafios',       icon: Flag,          label: 'Desafios' },
  { to: '/gestao/reunioes',       icon: Presentation,  label: 'Reuniões' },
  MANUAL_RECEPCAO_ITEM,
]);

const profNav = [
  { to: '/gestao/meu-desempenho',    icon: TrendingUp, label: 'Meu Desempenho' },
  { to: '/gestao/duelos',            icon: Sword,      label: 'Duelos da Semana' },
  { to: '/gestao/ranking',           icon: Trophy,     label: 'Ranking' },
  { to: '/gestao/aniversarios',      icon: Cake,       label: 'Aniversários' },
  { to: '/gestao/minhas-conquistas', icon: Award,      label: 'Minhas Conquistas' },
  { to: '/gestao/meta-realizado',    icon: Target,     label: 'Meta x Realizado' },
];

// Nav da Recepcionista — só operação da unidade dela.
// Vê: agenda, financeiro (caixas + comandas), ranking/duelos (motivacional).
// 09/10/2026 (dono, H2 — FIN_TRILHO_UNIDADE com OCULTAR_NATIVO): a recepção
// perde o "Caixa & Comandas" (operação nativa escondida); a rota continua viva
// por URL (App.jsx), nada foi apagado.
const RECEPCAO_SEM_CAIXA = FIN_TRILHO_UNIDADE && OCULTAR_NATIVO;
const recepcionistaNav = filtrarNavNativo([
  { to: '/gestao/meu-desempenho', icon: TrendingUp,    label: 'Meu Desempenho' },
  MANUAL_RECEPCAO_ITEM,
  { to: '/gestao/agenda',         icon: CalendarDays,  label: 'Agenda' },
  { to: '/gestao/financeiro',     icon: DollarSign,    label: 'Caixa & Comandas' },
  { to: '/gestao/radar',          icon: Radar,         label: 'Radar de Clientes' },
  { to: '/gestao/privates',       icon: Crown,         label: 'Feel King Private' },
  // meta-realizado (não /metas): recepcionista navega no ramo 'professional'
  // das rotas — /metas só existe no ramo admin (22/08/2026)
  { to: '/gestao/meta-realizado', icon: Target,        label: 'Meta x Realizado' },
  { to: '/gestao/ranking',        icon: Trophy,        label: 'Ranking' },
  { to: '/gestao/duelos',         icon: Sword,         label: 'Duelos da Semana' },
  { to: '/gestao/aniversarios',   icon: Cake,          label: 'Aniversários' },
]).filter(item => !(RECEPCAO_SEM_CAIXA && item.to === '/gestao/financeiro'));

function FKLogo() {
  return <DNALogo tone="light" width={112} />;
}

// Item simples (link direto). `badge` (opcional): número numa pílula
// vermelha à direita ("99+" acima de 99; some com 0) — o Financeiro (alertas
// graves+médios da Auditoria, 07/10/2026) e, desde 16/10/2026, Aniversários
// (aniversariantes de hoje, até abrir a página). `badgeTitle`: dica da pílula.
function NavItem({ to, icon: Icon, label, onClose, badge, badgeTitle }) {
  const txtBadge = textoBadge(badge);
  // Prefetch do chunk da rota ao passar mouse/foco — o user costuma
  // demorar 100-500ms entre hover e click, suficiente pra baixar
  // o chunk em paralelo e eliminar o fallback "carregando".
  const handlePrefetch = () => prefetchRoute(to);
  return (
    <NavLink
      to={to}
      onClick={onClose}
      onPointerEnter={handlePrefetch}
      onFocus={handlePrefetch}
      onTouchStart={handlePrefetch}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-180 ease-fk ${
          isActive
            ? 'bg-white/10 text-white'
            : 'text-[#A3B1C6] hover:text-white hover:bg-white/5'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {/* Active indicator bar à esquerda */}
          <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-[2px] rounded-r-full bg-brand-300 transition-all duration-180 ease-fk ${
            isActive ? 'h-5 opacity-100' : 'h-0 opacity-0'
          }`} />
          <Icon size={16} strokeWidth={1.75} className={isActive ? 'text-brand-300' : 'text-[#8C9CB5] group-hover:text-white transition-colors'} />
          {label}
          {txtBadge && (
            <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-bold leading-[18px] text-center tabular-nums"
              title={badgeTitle || 'Alertas da auditoria para conferir'}>
              {txtBadge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

// Grupo expansível com children
function NavGroup({ icon: Icon, label, children, currentPath, onClose, badgeDoFilho = null }) {
  // Marca o grupo como ativo se a rota atual é uma das children
  const childPaths = useMemo(() => new Set(children.map(c => c.to)), [children]);
  const hasActiveChild = childPaths.has(currentPath);
  const [open, setOpen] = useState(hasActiveChild);

  // Quando a rota muda pra dentro do grupo, abre automaticamente
  React.useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={`group flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-180 ease-fk ${
          hasActiveChild
            ? 'text-white'
            : 'text-[#A3B1C6] hover:text-white hover:bg-white/5'
        }`}
      >
        <Icon size={16} strokeWidth={1.75} className={hasActiveChild ? 'text-brand-300' : 'text-[#8C9CB5] group-hover:text-white transition-colors'} />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown
          size={13}
          strokeWidth={2}
          className={`text-[#8C9CB5] transition-transform duration-280 ease-fk ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="ml-[18px] pl-3 mt-0.5 mb-1 border-l border-white/8 space-y-px animate-slide-up-fade">
          {children.map(child => (
            <NavLink
              key={child.to}
              to={child.to}
              onClick={onClose}
              onPointerEnter={() => prefetchRoute(child.to)}
              onFocus={() => prefetchRoute(child.to)}
              onTouchStart={() => prefetchRoute(child.to)}
              className={({ isActive }) =>
                `block px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-180 ease-fk ${
                  isActive
                    ? 'text-white bg-white/10'
                    : 'text-[#8C9CB5] hover:text-white hover:bg-white/5'
                }`
              }
            >
              <span className="flex items-center gap-2">
                <span className="flex-1 min-w-0 truncate">{child.label}</span>
                {/* 26/10/2026: os pedidos esperando a recepção ao lado de "CRM" */}
                {textoBadge(badgeDoFilho ? badgeDoFilho(child) : undefined) && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-bold leading-[18px] text-center tabular-nums" title="Pedidos esperando a recepção">
                    {textoBadge(badgeDoFilho(child))}
                  </span>
                )}
              </span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ open, onClose }) {
  const { user, logout, isMaster } = useAuth();
  const dataCtx = useData();
  const professionals = dataCtx?.professionals || [];
  const navigate = useNavigate();
  const location = useLocation();
  const myProf = user?.professionalId ? professionals.find(p => p.id === user.professionalId) : null;
  const isGerente = myProf?.role === 'Gerente';
  const isSocio = myProf?.role === 'Sócio';
  const isRecepcionista = myProf?.role === 'Recepcionista';
  const isMarketing = myProf?.role === 'Marketing';
  // Recibos da folha (05/10/2026): só a gerente LIBERADA no servidor ganha o
  // "Banco de Horas" (página só com os recibos das unidades dela). Enquanto a
  // resposta não chega, pode=false → nenhuma outra gerente vê o item piscar.
  const usaGerenteNav = user?.role === 'admin' && isGerente; // = baseNav é o gerenteNav
  const recibosAcesso = useRecibosAcesso({ enabled: usaGerenteNav });

  const baseNav = user?.role === 'admin'
    ? (
        isRecepcionista ? recepcionistaNav
        : isMarketing ? marketingNav
        : isSocio ? socioNav
        : isGerente ? gerenteNav
        : adminNav
      )
    : (isRecepcionista ? recepcionistaNav : profNav);
  // Financeiro (06/10/2026 — lista fechada): no adminNav o item só aparece se
  // o SERVIDOR disser que a conta é operadora (master nem espera). Enquanto
  // confere, operador=false → o item não pisca pra quem não é (ex.: Assistente
  // Adm). A recepção tem o próprio "Caixa & Comandas" (recepcionistaNav).
  const usaAdminNav = baseNav === adminNav;
  // Pré-agendamentos da IA de atendimento (13/10/2026): para gerente e recepção o
  // item só aparece quando o SERVIDOR libera (/api/atendimento-fila?op=me; no
  // rollout 'master', que é o padrão, nunca aparece para elas).
  const usaFilaNav = IA_ATENDENTE_MENU && (baseNav === gerenteNav || baseNav === recepcionistaNav);
  const atdAcesso = useAtendimentoAcesso({ enabled: usaFilaNav });
  // 26/10/2026 (decisão do dono): o número de pedidos esperando a recepção ao lado do CRM da Sofia, para a
  // recepção não deixar passar um agendamento. Só números (o escopo é do servidor), a cada 60 s com a aba visível.
  const comCrm = CRM_SOFIA_MENU && IA_ATENDENTE_MENU && ((usaFilaNav && atdAcesso.podeFila) || isMaster);
  const filaContador = useFilaContador({ enabled: comCrm });
  const operadorFin = useOperadorFinanceiro({ enabled: usaAdminNav });
  // Auditoria do Financeiro (07/10/2026): número de alertas graves+médios ao
  // lado de "Financeiro" — só para o operador (mesma loja da tela, 1 pedido).
  const finAlertas = useFinAlertas({ enabled: usaAdminNav && operadorFin.operador });
  // Aniversário de hoje (16/10/2026): pílula no item "Aniversários" de TODOS os
  // menus até a pessoa abrir a página (regra em src/utils/useAvisoAniversario.js).
  const avisoAniv = useAvisoAniversario({ professionals, userId: user?.id });
  // Filtra itens masterOnly se não-master
  let nav = baseNav
    .filter(item => !item.masterOnly || isMaster || (item.reunioes && podeReunioes(user, isGerente)))
    .filter(item => !(usaAdminNav && item.to === '/gestao/financeiro' && !operadorFin.operador))
    .map(item => item.children
      ? { ...item, children: item.children.filter(c => !c.masterOnly || isMaster) }
      : item);

  // Gerente liberada → "Banco de Horas" (recibos da folha) logo após Profissionais
  if (usaGerenteNav && recibosAcesso.pode && !nav.some(i => i.to === '/gestao/banco-horas')) {
    const bhItem = { to: '/gestao/banco-horas', icon: Clock, label: 'Banco de Horas' };
    const profIdx = nav.findIndex(i => i.to === '/gestao/profissionais');
    nav = profIdx >= 0
      ? [...nav.slice(0, profIdx + 1), bhItem, ...nav.slice(profIdx + 1)]
      : [...nav, bhItem];
  }

  // CRM da Sofia (gerente e recepção liberadas no servidor), logo após o Radar. 26/10/2026: no lugar dos
  // Pré-agendamentos (o pedido fica dentro da conversa); sem o CRM no ar, a fila antiga de sempre.
  if (usaFilaNav && atdAcesso.podeFila && !nav.some(i => i.to === ROTA_CRM || i.to === '/gestao/pre-agendamentos')) {
    const filaItem = CRM_SOFIA_MENU ? { to: ROTA_CRM, icon: Inbox, label: 'CRM da Sofia' } : { to: '/gestao/pre-agendamentos', icon: Inbox, label: 'Pré-agendamentos' };
    const radarIdx = nav.findIndex(i => i.to === '/gestao/radar');
    nav = radarIdx >= 0
      ? [...nav.slice(0, radarIdx + 1), filaItem, ...nav.slice(radarIdx + 1)]
      : [...nav, filaItem];
  }

  // "Bater Ponto" + "Meu Ponto" — só pra colaboradores CLT (não-PJ), acima do Ranking
  if (myProf && isPontoEligible(myProf)) {
    const pontoItems = [
      { to: '/gestao/bater-ponto', icon: Fingerprint, label: 'Bater Ponto' },
      { to: '/gestao/meu-ponto',   icon: Clock,       label: 'Meu Ponto' },
    ];
    const rankIdx = nav.findIndex(i => i.to === '/gestao/ranking');
    nav = rankIdx >= 0
      ? [...nav.slice(0, rankIdx), ...pontoItems, ...nav.slice(rankIdx)]
      : [...pontoItems, ...nav];
  }

  // "Ranking da Unidade" — logo abaixo do Ranking Geral, pra todos os cargos
  // que veem o ranking (todos veem ambos; unidades separadas, todas visíveis).
  {
    const ruIdx = nav.findIndex(i => i.to === '/gestao/ranking');
    if (ruIdx >= 0 && !nav.some(i => i.to === '/gestao/ranking-unidade')) {
      const ruItem = { to: '/gestao/ranking-unidade', icon: Building2, label: 'Ranking da Unidade' };
      nav = [...nav.slice(0, ruIdx + 1), ruItem, ...nav.slice(ruIdx + 1)];
    }
  }

  // Gerente legada (conta 'professional' com cargo Gerente) recebe o profNav dos
  // barbeiros: o Manual da Recepção entra no fim SÓ para ela (profNav não muda).
  if (user?.role !== 'admin' && isGerente && !nav.some(i => i.to === MANUAL_RECEPCAO_ROTA)) {
    nav = [...nav, MANUAL_RECEPCAO_ITEM];
  }

  // Feel King Private escondido (16/10/2026, OCULTAR_PRIVATES): sai do menu de
  // TODOS os cargos, inclusive o master (que ainda abre pelo link). As linhas
  // das listas acima ficam (reversível em featureFlags.js).
  if (OCULTAR_PRIVATES) {
    nav = nav
      .filter(item => !ehRotaPrivates(item.to))
      .map(item => item.children
        ? { ...item, children: item.children.filter(c => !ehRotaPrivates(c.to)) }
        : item);
  }

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-20 md:hidden" onClick={onClose} />
      )}
      <aside className={`
        fixed md:static inset-y-0 left-0 z-30 w-64
        bg-ink border-r border-white/8
        flex flex-col transition-transform duration-300 ease-fk
        ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>

        {/* Logo */}
        <div className="flex items-center justify-between px-5 py-5">
          <FKLogo />
          <button onClick={onClose} className="md:hidden text-[#8C9CB5] hover:text-white transition-colors w-10 h-10 -mr-2 flex items-center justify-center rounded-md hover:bg-white/5">
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* User info */}
        <div className="px-4 pb-3">
          <div className="bg-white/[0.04] rounded-card px-3 py-3">
            <div className="text-white text-[13px] font-medium truncate leading-tight">{user?.name}</div>
            <div className="text-[#8C9CB5] text-[11px] truncate mt-0.5">{user?.email}</div>
            <div className="inline-flex items-center mt-2 text-[10px] bg-brand-300/15 text-brand-300 px-2 py-0.5 rounded-md tracking-wide font-medium">
              {isRecepcionista ? 'Recepcionista' : isMarketing ? 'Marketing' : isSocio ? 'Sócio' : isGerente ? 'Gerente' : (user?.role === 'admin' ? 'Administrador' : 'Profissional')}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-3 space-y-px overflow-y-auto">
          {nav.map((item, idx) => {
            if (item.children) {
              return (
                <NavGroup
                  key={item.label}
                  icon={item.icon}
                  label={item.label}
                  children={item.children}
                  currentPath={location.pathname}
                  onClose={onClose}
                  badgeDoFilho={(c) => (c.to === ROTA_CRM ? filaContador.badge : undefined)}
                />
              );
            }
            return (
              <NavItem
                key={item.to || idx}
                to={item.to}
                icon={item.icon}
                label={item.label}
                onClose={onClose}
                badge={usaAdminNav && item.to === '/gestao/financeiro' ? finAlertas.contagem?.badge : item.to === ROTA_ANIVERSARIOS ? avisoAniv.badge : item.to === ROTA_CRM ? filaContador.badge : undefined}
                badgeTitle={item.to === ROTA_ANIVERSARIOS ? 'Aniversário hoje' : item.to === ROTA_CRM ? 'Pedidos esperando a recepção' : undefined}
              />
            );
          })}
        </nav>

        {/* Logout */}
        <div className="px-2 pb-4 pt-3">
          <button
            onClick={handleLogout}
            className="group flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-[13px] font-medium text-[#8C9CB5] hover:text-danger hover:bg-danger/10 transition-all duration-180 ease-fk"
          >
            <LogOut size={16} strokeWidth={1.75} className="group-hover:text-danger transition-colors" />
            Sair da conta
          </button>
        </div>

      </aside>
    </>
  );
}
